import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, Package, Inbox, LogOut, Plus, Pencil, Trash2, X, ExternalLink, Download, Link2, Loader2, Search, KeyRound } from "lucide-react";
import api, { CATEGORY_LABELS, formatPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const EMPTY = { category: "kola", brand: "", title: "", price: 0, vat: true, condition: "pouzite", description: "", images: [], size: "", featured: false };

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("prehled");
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [importMode, setImportMode] = useState(null); // 'single' | 'profile'
  const [listingSearch, setListingSearch] = useState("");
  const [listingCat, setListingCat] = useState("");
  const [pwOpen, setPwOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/listings").then((r) => setListings(r.data)).catch(() => {});
    api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = () => { logout(); navigate("/"); };

  const deleteListing = async (id) => {
    if (!window.confirm("Opravdu smazat tento inzerát?")) return;
    await api.delete(`/listings/${id}`);
    toast.success("Inzerát smazán");
    load();
  };

  const markRead = async (id) => { await api.patch(`/inquiries/${id}`); load(); };
  const deleteInquiry = async (id) => { await api.delete(`/inquiries/${id}`); toast.success("Poptávka smazána"); load(); };

  const navItems = [
    { key: "prehled", label: "Přehled", icon: LayoutGrid },
    { key: "inzeraty", label: "Inzeráty", icon: Package },
    { key: "poptavky", label: "Poptávky", icon: Inbox },
  ];

  return (
    <div className="min-h-screen relative z-10 flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-72 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-white/10 bg-[#080808] p-6 flex flex-col">
        <div className="font-display text-2xl mb-1">ALUKOLA<span className="text-[#ff3b30]">-BAZAR</span></div>
        <div className="text-white/40 text-xs mb-8">{user?.email}</div>
        <nav className="flex lg:flex-col gap-2 flex-wrap">
          {navItems.map((n) => (
            <button
              key={n.key}
              data-testid={`admin-tab-${n.key}`}
              onClick={() => setTab(n.key)}
              className={`flex items-center gap-3 px-4 py-3 font-head text-sm uppercase tracking-wider transition-colors ${
                tab === n.key ? "bg-[#ff3b30] text-white" : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <n.icon size={16} /> {n.label}
              {n.key === "poptavky" && stats?.new_inquiries > 0 && (
                <span className="ml-auto bg-white text-black text-[10px] px-1.5 py-0.5 font-bold">{stats.new_inquiries}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-1">
          <button onClick={() => setPwOpen(true)} data-testid="admin-change-password" className="flex items-center gap-3 px-4 py-3 font-head text-sm uppercase tracking-wider text-white/50 hover:text-white transition-colors">
            <KeyRound size={16} /> Změnit heslo
          </button>
          <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-3 px-4 py-3 font-head text-sm uppercase tracking-wider text-white/50 hover:text-[#ff3b30] transition-colors">
            <LogOut size={16} /> Odhlásit
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 p-6 md:p-10">
        {tab === "prehled" && stats && (
          <div>
            <h1 className="font-display text-4xl md:text-5xl mb-10">PŘEHLED</h1>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
              <Stat label="Inzeráty celkem" value={stats.listings} />
              <Stat label="Poptávky" value={stats.inquiries} />
              <Stat label="Nové poptávky" value={stats.new_inquiries} accent />
              <Stat label="Alu kola" value={stats.by_category?.kola || 0} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <div key={k} className="bg-[#0d0d0d] p-6">
                  <div className="font-head uppercase tracking-[0.2em] text-xs text-white/40 mb-2">{v}</div>
                  <div className="font-display text-4xl">{stats.by_category?.[k] || 0}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "inzeraty" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <h1 className="font-display text-4xl md:text-5xl">INZERÁTY</h1>
              <div className="flex flex-wrap gap-2">
                <button data-testid="admin-import-single" onClick={() => setImportMode("single")} className="flex items-center gap-2 font-head uppercase tracking-wider text-sm border border-white/20 text-white px-4 py-3 hover:border-[#ff3b30] hover:text-[#ff3b30] transition-colors">
                  <Link2 size={16} /> Import z odkazu
                </button>
                <button data-testid="admin-import-profile" onClick={() => setImportMode("profile")} className="flex items-center gap-2 font-head uppercase tracking-wider text-sm border border-white/20 text-white px-4 py-3 hover:border-[#ff3b30] hover:text-[#ff3b30] transition-colors">
                  <Download size={16} /> Import z profilu Sbazar
                </button>
                <button data-testid="admin-add-listing" onClick={() => setEditing(EMPTY)} className="flex items-center gap-2 font-head uppercase tracking-wider text-sm bg-[#ff3b30] text-white px-5 py-3 hover:bg-white hover:text-black transition-colors">
                  <Plus size={16} /> Nový inzerát
                </button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  data-testid="admin-listing-search"
                  value={listingSearch}
                  onChange={(e) => setListingSearch(e.target.value)}
                  placeholder="Hledat podle názvu, značky, rozměru…"
                  className="w-full bg-[#111] border border-[#222] pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]"
                />
              </div>
              <select
                data-testid="admin-listing-cat"
                value={listingCat}
                onChange={(e) => setListingCat(e.target.value)}
                className="bg-[#111] border border-[#222] px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]"
              >
                <option value="">Všechny kategorie</option>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>

            {(() => {
              const q = listingSearch.trim().toLowerCase();
              const visible = listings.filter(
                (l) =>
                  (!listingCat || l.category === listingCat) &&
                  (!q || `${l.title} ${l.brand} ${l.size} ${CATEGORY_LABELS[l.category]}`.toLowerCase().includes(q))
              );
              return (
                <>
                  <div className="text-white/40 text-xs mb-3" data-testid="admin-listing-count">
                    Zobrazeno {visible.length} z {listings.length} inzerátů
                  </div>
                  <div className="border border-white/10 divide-y divide-white/10">
                    {visible.length === 0 ? (
                      <div className="p-6 text-white/40 text-sm" data-testid="admin-listing-empty">Nic nenalezeno.</div>
                    ) : (
                      visible.map((l) => (
                        <div key={l.id} data-testid={`admin-listing-${l.id}`} className="flex items-center gap-4 p-4 bg-[#0d0d0d] hover:bg-[#131313] transition-colors">
                          <img src={l.images?.[0]} alt="" className="w-16 h-16 object-cover border border-white/10" />
                          <div className="flex-1 min-w-0">
                            <div className="font-head font-semibold truncate">{l.title}</div>
                            <div className="text-white/40 text-xs">{CATEGORY_LABELS[l.category]} · {l.brand} {l.size && `· ${l.size}`} {l.featured && "· ★ doporučeno"}</div>
                          </div>
                          <div className="font-display text-lg hidden md:block">{formatPrice(l.price)}</div>
                          <button data-testid={`admin-edit-${l.id}`} onClick={() => setEditing(l)} className="p-2 text-white/60 hover:text-white"><Pencil size={16} /></button>
                          <button data-testid={`admin-delete-${l.id}`} onClick={() => deleteListing(l.id)} className="p-2 text-white/60 hover:text-[#ff3b30]"><Trash2 size={16} /></button>
                        </div>
                      ))
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {tab === "poptavky" && (
          <div>
            <h1 className="font-display text-4xl md:text-5xl mb-8">POPTÁVKY</h1>
            {inquiries.length === 0 ? (
              <p className="text-white/40">Zatím žádné poptávky.</p>
            ) : (
              <div className="space-y-4">
                {inquiries.map((q) => (
                  <div key={q.id} data-testid={`admin-inquiry-${q.id}`} className={`border p-5 bg-[#0d0d0d] ${q.status === "new" ? "border-[#ff3b30]/40" : "border-white/10"}`}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="font-head font-semibold text-lg">{q.name} {q.status === "new" && <span className="text-[#ff3b30] text-xs align-middle">● NOVÁ</span>}</div>
                        <div className="text-white/50 text-sm">
                          <a href={`mailto:${q.email}`} className="hover:text-white">{q.email}</a>
                          {q.phone && <> · <a href={`tel:${q.phone}`} className="hover:text-white">{q.phone}</a></>}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {q.status === "new" && <button onClick={() => markRead(q.id)} className="text-xs font-head uppercase tracking-wider border border-white/20 px-3 py-1.5 hover:border-white">Přečteno</button>}
                        <button onClick={() => deleteInquiry(q.id)} className="p-1.5 text-white/50 hover:text-[#ff3b30]"><Trash2 size={15} /></button>
                      </div>
                    </div>
                    {q.listing_title && <div className="text-xs text-[#ff3b30] mb-2 flex items-center gap-1"><ExternalLink size={12} /> {q.listing_title}</div>}
                    <p className="text-white/70 text-sm whitespace-pre-line">{q.message}</p>
                    <div className="text-white/30 text-xs mt-3">{new Date(q.created_at).toLocaleString("cs-CZ")}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {editing && <ListingModal listing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {importMode && (
        <ImportModal
          mode={importMode}
          onClose={() => setImportMode(null)}
          onEditDraft={(d) => { setImportMode(null); setEditing(d); }}
          onImported={() => { setImportMode(null); load(); }}
        />
      )}
      {pwOpen && <PasswordModal onClose={() => setPwOpen(false)} />}
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="bg-[#0d0d0d] p-6">
    <div className="font-head uppercase tracking-[0.15em] text-[11px] text-white/40 mb-2">{label}</div>
    <div className={`font-display text-4xl ${accent ? "text-[#ff3b30]" : ""}`}>{value}</div>
  </div>
);

function ListingModal({ listing, onClose, onSaved }) {
  const [form, setForm] = useState({ ...listing, images: listing.images || [] });
  const [imagesText, setImagesText] = useState((listing.images || []).join("\n"));
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const images = imagesText.split("\n").map((s) => s.trim()).filter(Boolean);
    const payload = { ...form, price: Number(form.price) || 0, images };
    try {
      if (listing.id) await api.put(`/listings/${listing.id}`, payload);
      else await api.post("/listings", payload);
      toast.success(listing.id ? "Inzerát upraven" : "Inzerát přidán");
      onSaved();
    } catch {
      toast.error("Uložení se nezdařilo");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full bg-[#111] border border-[#222] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]";

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 py-10">
      <div className="w-full max-w-2xl border border-white/15 bg-[#0d0d0d]" data-testid="listing-modal">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="font-display text-2xl">{listing.id ? "UPRAVIT INZERÁT" : "NOVÝ INZERÁT"}</h2>
          <button onClick={onClose} data-testid="modal-close" className="text-white/60 hover:text-white"><X /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-white/40 uppercase tracking-wider">Kategorie</label>
              <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={field}>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-white/40 uppercase tracking-wider">Stav</label>
              <select data-testid="form-condition" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className={field}>
                <option value="pouzite">Použité</option>
                <option value="nove">Nové</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider">Značka *</label>
            <input required data-testid="form-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="např. Audi" className={field} />
          </div>
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider">Název *</label>
            <input required data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={'např. Audi alu kola 18"'} className={field} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-white/40 uppercase tracking-wider">Cena (Kč) *</label>
              <input required type="number" data-testid="form-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={field} />
            </div>
            <div>
              <label className="text-xs text-white/40 uppercase tracking-wider">Rozměr / rozteč</label>
              <input data-testid="form-size" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder={'18" 5x112'} className={field} />
            </div>
          </div>
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider">Popis</label>
            <textarea rows={4} data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${field} resize-none`} />
          </div>
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider">Odkazy na obrázky (každý na nový řádek)</label>
            <textarea rows={3} data-testid="form-images" value={imagesText} onChange={(e) => setImagesText(e.target.value)} placeholder="https://…" className={`${field} resize-none`} />
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="form-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-[#ff3b30]" />
            <span className="text-sm text-white/70">Zobrazit jako doporučené na úvodní stránce</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="form-vat" checked={!!form.vat} onChange={(e) => setForm({ ...form, vat: e.target.checked })} className="w-4 h-4 accent-[#ff3b30]" />
            <span className="text-sm text-white/70">Zobrazit rozpad DPH (cena je zadána bez DPH, přičíst 21 %)</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} data-testid="form-submit" className="flex-1 font-head uppercase tracking-wider bg-[#ff3b30] text-white py-3.5 hover:bg-white hover:text-black transition-colors disabled:opacity-50">
              {saving ? "Ukládání…" : "Uložit"}
            </button>
            <button type="button" onClick={onClose} className="font-head uppercase tracking-wider border border-white/20 px-6 hover:border-white transition-colors">Zrušit</button>
          </div>
        </form>
      </div>
    </div>
  );
}


function ImportModal({ mode, onClose, onEditDraft, onImported }) {
  const [url, setUrl] = useState("");
  const [importCat, setImportCat] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);

  const loadSingle = async () => {
    const lines = url.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (lines.length === 0) return;
    if (lines.length === 1) {
      setLoading(true);
      try {
        const { data } = await api.post("/import/sbazar/preview", { url: lines[0], category: importCat || undefined });
        onEditDraft(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Načtení se nezdařilo");
      } finally {
        setLoading(false);
      }
    } else {
      setImporting(true);
      try {
        const { data } = await api.post("/import/sbazar/bulk", { urls: lines, category: importCat || undefined });
        toast.success(`Naimportováno ${data.created} inzerátů${data.skipped ? `, přeskočeno ${data.skipped}` : ""}`);
        onImported();
      } catch {
        toast.error("Import se nezdařil");
      } finally {
        setImporting(false);
      }
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/import/sbazar/profile", { url });
      setItems(data.items);
      const sel = {};
      data.items.forEach((it) => { if (!it.already_imported) sel[it.source_id] = true; });
      setSelected(sel);
      if (data.items.length === 0) toast.info("Na profilu se nenašly žádné inzeráty");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Načtení profilu se nezdařilo");
    } finally {
      setLoading(false);
    }
  };

  const doBulk = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return toast.error("Vyberte alespoň jeden inzerát");
    setImporting(true);
    try {
      const { data } = await api.post("/import/sbazar/bulk", { ids, category: importCat || undefined });
      toast.success(`Naimportováno ${data.created} inzerátů${data.skipped ? `, přeskočeno ${data.skipped}` : ""}`);
      onImported();
    } catch {
      toast.error("Import se nezdařil");
    } finally {
      setImporting(false);
    }
  };

  const field = "w-full bg-[#111] border border-[#222] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]";
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 py-10">
      <div className="w-full max-w-3xl border border-white/15 bg-[#0d0d0d]" data-testid="import-modal">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="font-display text-2xl">{mode === "single" ? "IMPORT Z ODKAZU" : "IMPORT Z PROFILU SBAZAR"}</h2>
          <button onClick={onClose} data-testid="import-close" className="text-white/60 hover:text-white"><X /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider">Zařadit do kategorie</label>
            <select
              data-testid="import-category"
              value={importCat}
              onChange={(e) => setImportCat(e.target.value)}
              className="w-full mt-1 bg-[#111] border border-[#222] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]"
            >
              <option value="">Automaticky (dle Sbazaru)</option>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <label className="text-xs text-white/40 uppercase tracking-wider">
            {mode === "single" ? "Odkaz(y) na inzerát(y) ze Sbazaru — jeden na řádek" : "Odkaz na profil prodejce (např. sbazar.cz/bazar/Martin1976)"}
          </label>

          {mode === "single" ? (
            <div className="space-y-3">
              <textarea
                data-testid="import-url"
                rows={5}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={"https://www.sbazar.cz/inzerat/...\nhttps://www.sbazar.cz/inzerat/...\nhttps://www.sbazar.cz/inzerat/..."}
                className={`${field} resize-none`}
              />
              <button
                data-testid="import-load"
                onClick={loadSingle}
                disabled={loading || importing || !url.trim()}
                className="w-full flex items-center justify-center gap-2 font-head uppercase tracking-wider bg-[#ff3b30] text-white py-3.5 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
              >
                {(loading || importing) ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                {url.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean).length > 1 ? "Importovat všechny" : "Načíst inzerát"}
              </button>
              <p className="text-white/40 text-sm">
                Vložíš-li <b>jeden</b> odkaz, otevře se formulář ke kontrole a uložení. Vložíš-li <b>více</b> odkazů (každý na nový řádek), naimportují se rovnou všechny najednou. Duplicity se přeskočí.
              </p>
            </div>
          ) : (
            <div className="flex gap-3">
              <input
                data-testid="import-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.sbazar.cz/bazar/..."
                className={field}
              />
              <button
                data-testid="import-load"
                onClick={loadProfile}
                disabled={loading || !url}
                className="shrink-0 flex items-center gap-2 font-head uppercase tracking-wider bg-[#ff3b30] text-white px-5 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                Načíst
              </button>
            </div>
          )}

          {mode === "profile" && items && (
            <div className="space-y-3">
              <div className="flex items-center justify-between border-t border-white/10 pt-4">
                <span className="text-sm text-white/60">Nalezeno {items.length} inzerátů · vybráno {selectedCount}</span>
                <div className="flex gap-2 text-xs">
                  <button onClick={() => { const s = {}; items.forEach((it) => s[it.source_id] = true); setSelected(s); }} className="border border-white/20 px-3 py-1.5 hover:border-white uppercase tracking-wider">Vybrat vše</button>
                  <button onClick={() => setSelected({})} className="border border-white/20 px-3 py-1.5 hover:border-white uppercase tracking-wider">Zrušit výběr</button>
                </div>
              </div>
              <div className="max-h-[45vh] overflow-y-auto divide-y divide-white/10 border border-white/10">
                {items.map((it) => (
                  <label key={it.source_id} data-testid={`import-item-${it.source_id}`} className="flex items-center gap-3 p-3 hover:bg-[#131313] cursor-pointer">
                    <input type="checkbox" checked={!!selected[it.source_id]} onChange={(e) => setSelected({ ...selected, [it.source_id]: e.target.checked })} className="w-4 h-4 accent-[#ff3b30]" />
                    <img src={it.images?.[0]} alt="" className="w-12 h-12 object-cover border border-white/10" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{it.title}</div>
                      <div className="text-white/40 text-xs">{CATEGORY_LABELS[it.category]} · {it.brand || "?"} {it.already_imported && "· již importováno"}</div>
                    </div>
                    <span className="font-display text-sm">{it.price ? formatPrice(it.price) : "Dohodou"}</span>
                  </label>
                ))}
              </div>
              <button
                data-testid="import-bulk-submit"
                onClick={doBulk}
                disabled={importing || selectedCount === 0}
                className="w-full flex items-center justify-center gap-2 font-head uppercase tracking-wider bg-[#ff3b30] text-white py-3.5 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
              >
                {importing ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                Importovat vybrané ({selectedCount})
              </button>
              <p className="text-white/40 text-xs">Pozn.: Sbazar načte ze serveru zpravidla prvních ~50–60 inzerátů z profilu. Zbytek můžete doimportovat jednotlivě přes „Import z odkazu".</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("Nové heslo musí mít alespoň 8 znaků");
    if (next !== confirm) return toast.error("Nová hesla se neshodují");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Heslo bylo změněno");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Změna hesla se nezdařila");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full bg-[#111] border border-[#222] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]";

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 py-10">
      <div className="w-full max-w-md border border-white/15 bg-[#0d0d0d]" data-testid="password-modal">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="font-display text-2xl">ZMĚNA HESLA</h2>
          <button onClick={onClose} data-testid="password-close" className="text-white/60 hover:text-white"><X /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider">Současné heslo</label>
            <input required type="password" data-testid="pw-current" value={current} onChange={(e) => setCurrent(e.target.value)} className={field} />
          </div>
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider">Nové heslo (min. 8 znaků)</label>
            <input required type="password" data-testid="pw-new" value={next} onChange={(e) => setNext(e.target.value)} className={field} />
          </div>
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider">Nové heslo znovu</label>
            <input required type="password" data-testid="pw-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={field} />
          </div>
          <button type="submit" disabled={saving} data-testid="pw-submit" className="w-full font-head uppercase tracking-wider bg-[#ff3b30] text-white py-3.5 hover:bg-white hover:text-black transition-colors disabled:opacity-50">
            {saving ? "Ukládání…" : "Změnit heslo"}
          </button>
        </form>
      </div>
    </div>
  );
}

